/**
 * officeNoteExtension — `IMailEditorInsert.insertOfficeNote(payload, isHTML)` 의 NgwEditor
 * extension (PR-N5).
 *
 * TinyMCE 평행: `nine_tinymce_desktop.js` L7369~L7429 의 `insertOfficeNoteItem` + `window.
 * insertOfficeNote` 가 큰 HTML 단편 (외곽 `div.nine-pg > a[href][contenteditable=false] > table`
 * + title/desc/date 자식) 을 selection 위치에 `format: isHTML ? 'html' : 'text'` 로 삽입.
 *
 * NgwEditor 측에서는 `office_note` 노드 (content: 'block+') 를 사용해 isHTML 분기를 흡수
 * (사용자 결정 (a)):
 *   - isHTML === true: payload.content 를 브라우저 `DOMParser` → ProseMirror `DOMParser.fromSchema`
 *     로 파싱해 노드 content 로 사용.
 *   - isHTML === false: payload.content 를 단일 paragraph 안의 text 로 변환.
 *
 * ### 보안 — XSS
 *
 * isHTML 분기의 `new DOMParser().parseFromString(..., 'text/html')` 는 `<script>` 비활성 +
 * `<iframe>` 무력화 (브라우저 표준). 추가로 ProseMirror parser 가 schema 외 노드를 필터링 —
 * mailRegions.ts 의 `insertMailLayout` 명령과 동일한 흐름.
 *
 * `title` / `url` 은 노드 attribute 로만 보존되며 `toDOM` 의 `data-*` attr 로 흐른다.
 */

import { DOMParser as PMDOMParser, type NgwExtension } from '@ngweditor/react'

interface OfficeNoteCommandPayload {
  content: string
  title?: string
  url?: string
}

/** payload.content HTML 단편을 ProseMirror parser 가 받을 컨테이너 DOM 으로 변환. */
function htmlToContainer(html: string): HTMLDivElement {
  const fragment = `<div id="ngw-office-note-container">${html}</div>`
  const browserDoc = new DOMParser().parseFromString(fragment, 'text/html')
  const container = browserDoc.getElementById('ngw-office-note-container') as HTMLDivElement | null
  if (container != null) return container
  return document.createElement('div')
}

export const officeNoteExtension: NgwExtension = {
  name: 'office-note',

  nodes: {
    office_note: {
      group: 'block',
      content: 'block+',
      attrs: {
        title: { default: '' },
        url: { default: '' },
      },
      parseDOM: [
        {
          tag: 'div.nine-office-note',
          getAttrs: (dom) => {
            if (typeof dom === 'string') return false
            return {
              title: dom.getAttribute('data-title') ?? '',
              url: dom.getAttribute('data-url') ?? '',
            }
          },
        },
      ],
      toDOM: (node) => [
        'div',
        {
          class: 'nine-office-note',
          'data-title': String(node.attrs.title ?? ''),
          'data-url': String(node.attrs.url ?? ''),
        },
        0,
      ],
    },
  },

  commands: {
    insertOfficeNote: (state, dispatch, _view, ...args) => {
      if (dispatch == null) return true

      const payloadArg = args[0]
      if (typeof payloadArg !== 'object' || payloadArg === null) return false
      const payload = payloadArg as OfficeNoteCommandPayload
      const isHTML = args[1] === true

      const { schema } = state
      const nodeType = schema.nodes.office_note
      if (nodeType == null) return false

      const rawContent = String(payload.content ?? '')
      const nodeAttrs: Record<string, unknown> = {
        title: payload.title ?? '',
        url: payload.url ?? '',
      }

      const officeNoteNode = (() => {
        if (isHTML === true) {
          const container = htmlToContainer(rawContent)
          const parser = PMDOMParser.fromSchema(schema)
          const parsed = parser.parse(container)
          if (parsed.content.childCount > 0) {
            return nodeType.create(nodeAttrs, parsed.content)
          }
          return nodeType.createAndFill(nodeAttrs)
        }
        // 텍스트 분기: 단일 paragraph 의 text 로 흡수
        const paraType = schema.nodes.paragraph
        if (paraType == null) return nodeType.createAndFill(nodeAttrs)
        const textNode = rawContent.length > 0 ? schema.text(rawContent) : null
        const para = textNode != null ? paraType.create(null, [textNode]) : paraType.createAndFill()
        if (para == null) return nodeType.createAndFill(nodeAttrs)
        return nodeType.create(nodeAttrs, [para])
      })()

      if (officeNoteNode == null) return false

      const tr = state.tr.replaceSelectionWith(officeNoteNode)
      dispatch(tr.scrollIntoView())
      return true
    },
  },
}
